/** True when the primary input is touch (phones/tablets) — not a touch-capable
 * laptop, whose primary pointer is still the mouse. */
export function isTouchDevice(): boolean {
  return (
    navigator.maxTouchPoints > 0 &&
    window.matchMedia('(pointer: coarse)').matches
  )
}

const JOY_RADIUS = 56 // px the thumb can travel
const DEADZONE = 0.18

/** Best-effort fullscreen toggle. Works on Android; iOS Safari has no element
 * fullscreen (throws) — caught so the anti-zoom viewport still does its job. */
export function toggleFullscreen(): void {
  try {
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => {})
      return
    }
    void document.documentElement
      .requestFullscreen()
      .then(() => {
        // Lock landscape where supported (Android). iOS has no lock — caught.
        try {
          void screen.orientation.lock('landscape').catch(() => {})
        } catch {
          /* no orientation lock */
        }
      })
      .catch(() => {})
  } catch {
    /* fullscreen unsupported (iOS) */
  }
}

interface MobileOptions {
  onScoreboard: () => void
  onThirdPerson: () => void
}

/**
 * On-screen touch controls for phones: a floating left-stick to move, drag the
 * right half of the screen to look, and a button cluster (fire / jump / duck /
 * reload / weapon). PlayerController polls the exposed state each frame.
 */
export default class MobileControls {
  // Movement stick, components in [-1, 1] (x = strafe right, y = forward).
  public readonly move = { x: 0, y: 0 }
  public fire = false // held
  public jump = false // held
  public crouch = false // toggle
  private reloadQueued = false
  private switchQueued = false
  private lookDX = 0
  private lookDY = 0

  private readonly root: HTMLDivElement
  private readonly joyBase: HTMLDivElement
  private readonly joyThumb: HTMLDivElement
  private joyId: number | null = null
  private joyCx = 0
  private joyCy = 0
  private lookId: number | null = null
  private lookX = 0
  private lookY = 0
  private fireId: number | null = null // the fire button doubles as a look surface
  private fireX = 0
  private fireY = 0

  constructor(private readonly opts: MobileOptions) {
    this.root = document.createElement('div')
    this.root.className = 'mobile hidden'

    // Left half: floating movement stick.
    const moveZone = document.createElement('div')
    moveZone.className = 'mobile__move'
    this.joyBase = document.createElement('div')
    this.joyBase.className = 'mobile__joy'
    this.joyThumb = document.createElement('div')
    this.joyThumb.className = 'mobile__joy-thumb'
    this.joyBase.appendChild(this.joyThumb)
    moveZone.appendChild(this.joyBase)

    // Right half: look-drag surface (behind the buttons).
    const lookZone = document.createElement('div')
    lookZone.className = 'mobile__look'

    // Button cluster.
    const fire = this.button('mobile__fire', 'FIRE')
    const jump = this.button('mobile__btn mobile__jump', 'JUMP')
    const duck = this.button('mobile__btn mobile__duck', 'DUCK')
    const reload = this.button('mobile__btn mobile__reload', 'R')
    const swap = this.button('mobile__btn mobile__swap', '1/3')
    const score = this.button('mobile__btn mobile__score', 'PING')
    const view = this.button('mobile__btn mobile__view', '1/3P')
    const fs = this.button('mobile__btn mobile__fs', '⛶')

    this.root.append(moveZone, lookZone, fire, jump, duck, reload, swap, score, view, fs)
    document.body.appendChild(this.root)

    // --- movement stick (floating) ---
    moveZone.addEventListener('pointerdown', (e) => {
      if (this.joyId !== null) return
      this.joyId = e.pointerId
      this.joyCx = e.clientX
      this.joyCy = e.clientY
      this.joyBase.style.left = `${e.clientX}px`
      this.joyBase.style.top = `${e.clientY}px`
      this.joyBase.classList.add('mobile__joy--on')
      moveZone.setPointerCapture(e.pointerId)
      e.preventDefault()
    })
    moveZone.addEventListener('pointermove', (e) => {
      if (e.pointerId !== this.joyId) return
      let dx = e.clientX - this.joyCx
      let dy = e.clientY - this.joyCy
      const len = Math.hypot(dx, dy)
      if (len > JOY_RADIUS) {
        dx = (dx / len) * JOY_RADIUS
        dy = (dy / len) * JOY_RADIUS
      }
      this.joyThumb.style.transform = `translate(${dx}px, ${dy}px)`
      const nx = dx / JOY_RADIUS
      const ny = dy / JOY_RADIUS
      this.move.x = Math.abs(nx) < DEADZONE ? 0 : nx
      this.move.y = Math.abs(ny) < DEADZONE ? 0 : -ny // screen-down → backward
    })
    const endJoy = (e: PointerEvent): void => {
      if (e.pointerId !== this.joyId) return
      this.joyId = null
      this.move.x = 0
      this.move.y = 0
      this.joyThumb.style.transform = 'translate(0,0)'
      this.joyBase.classList.remove('mobile__joy--on')
    }
    moveZone.addEventListener('pointerup', endJoy)
    moveZone.addEventListener('pointercancel', endJoy)

    // --- look drag ---
    lookZone.addEventListener('pointerdown', (e) => {
      if (this.lookId !== null) return
      this.lookId = e.pointerId
      this.lookX = e.clientX
      this.lookY = e.clientY
      lookZone.setPointerCapture(e.pointerId)
      e.preventDefault()
    })
    lookZone.addEventListener('pointermove', (e) => {
      if (e.pointerId !== this.lookId) return
      this.lookDX += e.clientX - this.lookX
      this.lookDY += e.clientY - this.lookY
      this.lookX = e.clientX
      this.lookY = e.clientY
    })
    const endLook = (e: PointerEvent): void => {
      if (e.pointerId === this.lookId) this.lookId = null
    }
    lookZone.addEventListener('pointerup', endLook)
    lookZone.addEventListener('pointercancel', endLook)

    // --- buttons ---
    this.fireButton(fire) // fire + drag-to-aim (slide the thumb while holding)
    this.hold(jump, (on) => (this.jump = on))
    this.tap(duck, () => {
      this.crouch = !this.crouch
      duck.classList.toggle('mobile__btn--on', this.crouch)
    })
    this.tap(reload, () => (this.reloadQueued = true))
    this.tap(swap, () => (this.switchQueued = true))
    this.tap(score, () => this.opts.onScoreboard())
    this.tap(view, () => this.opts.onThirdPerson())
    this.tap(fs, () => toggleFullscreen())
  }

  /** The fire button: holds fire, and while held a drag pans the aim too (so you
   * can shoot and aim with the same thumb). */
  private fireButton(el: HTMLElement): void {
    el.addEventListener('pointerdown', (e) => {
      this.fire = true
      this.fireId = e.pointerId
      this.fireX = e.clientX
      this.fireY = e.clientY
      el.classList.add('mobile__btn--press')
      el.setPointerCapture(e.pointerId)
      e.preventDefault()
      e.stopPropagation()
    })
    el.addEventListener('pointermove', (e) => {
      if (e.pointerId !== this.fireId) return
      this.lookDX += e.clientX - this.fireX
      this.lookDY += e.clientY - this.fireY
      this.fireX = e.clientX
      this.fireY = e.clientY
    })
    const up = (e: PointerEvent): void => {
      if (e.pointerId !== this.fireId) return
      this.fire = false
      this.fireId = null
      el.classList.remove('mobile__btn--press')
    }
    el.addEventListener('pointerup', up)
    el.addEventListener('pointercancel', up)
  }

  private button(className: string, label: string): HTMLDivElement {
    const b = document.createElement('div')
    b.className = className
    b.textContent = label
    return b
  }

  /** Held button: state true while pressed. */
  private hold(el: HTMLElement, set: (on: boolean) => void): void {
    el.addEventListener('pointerdown', (e) => {
      set(true)
      el.classList.add('mobile__btn--press')
      el.setPointerCapture(e.pointerId)
      e.preventDefault()
      e.stopPropagation()
    })
    const up = (): void => {
      set(false)
      el.classList.remove('mobile__btn--press')
    }
    el.addEventListener('pointerup', up)
    el.addEventListener('pointercancel', up)
  }

  /** Edge button: fires once on press. */
  private tap(el: HTMLElement, fn: () => void): void {
    el.addEventListener('pointerdown', (e) => {
      fn()
      el.classList.add('mobile__btn--press')
      e.preventDefault()
      e.stopPropagation()
    })
    const up = (): void => el.classList.remove('mobile__btn--press')
    el.addEventListener('pointerup', up)
    el.addEventListener('pointercancel', up)
  }

  public setActive(active: boolean): void {
    this.root.classList.toggle('hidden', !active)
  }

  /** Accumulated look delta (px) since the last call; resets. */
  public consumeLook(): { dx: number; dy: number } {
    const out = { dx: this.lookDX, dy: this.lookDY }
    this.lookDX = 0
    this.lookDY = 0
    return out
  }

  public consumeReload(): boolean {
    const q = this.reloadQueued
    this.reloadQueued = false
    return q
  }

  public consumeSwitch(): boolean {
    const q = this.switchQueued
    this.switchQueued = false
    return q
  }

  public dispose(): void {
    this.root.remove()
  }
}
